/**
 * Drilldown de un perfil de configuracion (ADR_012 DFCMGMT-05).
 *
 * Header con info del perfil + acciones (Pausar/Resume, Snapshot ahora, Borrar).
 * 3 KPIs: equipos, cambios sin revisar, ultimo evento.
 * Tabs: Equipos / Configuracion (read-only).
 * En tab Equipos: lista de devices del perfil; click abre drilldown
 * lateral del device (historial + diff + mark good — reutiliza ADR_011).
 *
 * Ruta: /admin/configuracion/:profileId
 *
 * Sub-vista del device: ?device=<id> query param. Si esta, se muestra la
 * vista detallada del device a la derecha; si no, empty state.
 */
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Star,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { describeCron } from "@/components/ScheduleBuilder";
import type {
  ConfigDevice,
  ConfigDeviceConfigSummary,
  DiffChange,
} from "@/api/types";
import {
  useConfigDevice,
  useConfigDeviceConfigs,
  useConfigDeviceDiff,
  useProfileDevices,
  useMarkGood,
  useTakeSnapshot,
} from "@/hooks/useConfig";
import {
  useConfigurationProfile,
  useDeleteProfile,
  usePauseProfile,
  useResumeProfile,
  useSnapshotNowProfile,
} from "@/hooks/useConfigProfiles";
import { useRollbackDevice, useRollbackProfile } from "@/hooks/useRollback";
import { cn } from "@/lib/utils";
import { RollbackDialog } from "@/components/RollbackDialog";
import { TablaComparativa } from "@/components/TablaComparativa";

// ────────────────────────────────────────────────────────────────────────────
// Helpers

function shortId(id: string): string {
  return id.slice(0, 8);
}

function formatPath(path: (string | number)[]): string {
  return path.length === 0 ? "(root)" : path.join(" › ");
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

// ────────────────────────────────────────────────────────────────────────────
// Componente principal

export function ConfigProfileDetailPage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDeviceId = searchParams.get("device");
  const [tab, setTab] = useState<"devices" | "configuration" | "comparativa">(
    "devices",
  );

  const profile = useConfigurationProfile(profileId ?? null);
  const pauseMut = usePauseProfile();
  const resumeMut = useResumeProfile();
  const deleteMut = useDeleteProfile();
  const snapshotMut = useSnapshotNowProfile();
  const rollbackProfileMut = useRollbackProfile(profileId ?? "");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [pausaDialogOpen, setPausaDialogOpen] = useState(false);
  const [pausaError, setPausaError] = useState<string | null>(null);
  const [rollbackError, setRollbackError] = useState<string | null>(null);

  const setSelectedDevice = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("device", id);
    else next.delete("device");
    setSearchParams(next, { replace: true });
  };

  if (profile.isPending && !profile.data) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!profile.data) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        Perfil no encontrado.
      </div>
    );
  }

  const p = profile.data;
  const isActive = p.status === "active";

  /**
   * Pausar apaga la cosecha de TODOS los equipos del perfil. Desde el
   * 1-sep-2026 pide el segundo factor: ese dia el perfil de HikCentral aparecio
   * en pausa y no hubo forma de saber quien lo hizo. El codigo identifica al
   * autor y el backend deja fila en la bitacora de administracion.
   *
   * Reanudar tambien lo pide: fue la reanudacion la que borro la ultima marca
   * de tiempo del suceso.
   */
  const handlePauseResume = async ({
    totpCode,
    notes,
  }: {
    totpCode: string;
    notes: string;
  }) => {
    setPausaError(null);
    setFeedback(null);
    const mut = isActive ? pauseMut : resumeMut;
    try {
      await mut.mutateAsync({
        profileId: p.id,
        totp_code: totpCode,
        notes: notes || null,
      });
      setPausaDialogOpen(false);
      setFeedback(isActive ? "Perfil pausado." : "Perfil reanudado.");
    } catch (err) {
      setPausaError(
        err instanceof ApiError ? err.message : "Error al cambiar estado.",
      );
    }
  };

  const handleSnapshot = async () => {
    setFeedback(null);
    try {
      const res = await snapshotMut.mutateAsync(p.id);
      setFeedback(
        `Capturados ${res.results.ok} OK · ${res.results.offline} offline · ${res.results.error} errores · ${res.results.dedupe} sin cambios.`,
      );
    } catch (err) {
      setFeedback(err instanceof ApiError ? err.message : "Error al capturar.");
    }
  };

  const handleRollback = async ({
    totpCode,
    notes,
  }: {
    totpCode: string;
    notes: string;
  }) => {
    setRollbackError(null);
    try {
      const res = await rollbackProfileMut.mutateAsync({
        totp_code: totpCode,
        target: "known_good",
        notes: notes || null,
      });
      setRollbackDialogOpen(false);
      setFeedback(
        `Rollback masivo agendado: ${res.devices_queued} equipos. Se aplicarán en el siguiente ciclo del worker.`,
      );
    } catch (err) {
      setRollbackError(
        err instanceof ApiError ? err.message : "Error al agendar rollback.",
      );
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        `¿Borrar el perfil "${p.name}"? Esto NO borra los snapshots históricos.`,
      )
    ) {
      return;
    }
    try {
      await deleteMut.mutateAsync(p.id);
      navigate("/admin/configuracion");
    } catch (err) {
      setFeedback(err instanceof ApiError ? err.message : "Error al borrar.");
    }
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => navigate("/admin/configuracion")}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-3" />
        Catálogo
      </button>

      <ProfileHeader
        name={p.name}
        description={p.description}
        deviceType={p.device_type}
        collectionMethod={p.collection_method}
        scheduleCron={p.schedule_cron}
        fieldsCount={p.fields_to_audit.length}
        status={p.status}
        onPauseResume={() => setPausaDialogOpen(true)}
        onSnapshot={handleSnapshot}
        onDelete={handleDelete}
        onRollback={() => {
          setRollbackError(null);
          setRollbackDialogOpen(true);
        }}
        snapshotPending={snapshotMut.isPending}
        toggleLoading={pauseMut.isPending || resumeMut.isPending}
        devicesCount={1 /* habilitado siempre — backend valida */}
      />

      <RollbackDialog
        open={rollbackDialogOpen}
        onClose={() => setRollbackDialogOpen(false)}
        onConfirm={handleRollback}
        isSubmitting={rollbackProfileMut.isPending}
        errorMessage={rollbackError}
        targetLabel={`${p.name} · perfil completo`}
        helperText="Cada equipo se revertirá a su snapshot known_good vigente. Si algún equipo no tiene known_good, será omitido."
      />

      <RollbackDialog
        open={pausaDialogOpen}
        onClose={() => setPausaDialogOpen(false)}
        onConfirm={handlePauseResume}
        isSubmitting={pauseMut.isPending || resumeMut.isPending}
        errorMessage={pausaError}
        title={isActive ? "Confirmar pausa" : "Confirmar reanudación"}
        confirmLabel={isActive ? "Pausar perfil" : "Reanudar perfil"}
        targetLabel={p.name}
        helperText={
          isActive
            ? "Se apagará la cosecha de TODOS los equipos de este perfil. Queda registrado quién lo hizo."
            : "Se reanudará la cosecha de este perfil. Queda registrado quién lo hizo."
        }
      />

      {feedback && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          {feedback}
        </div>
      )}

      <TabsNav tab={tab} setTab={setTab} />

      {tab === "devices" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <ProfileDevicesPanel
              profileId={p.id}
              selectedDeviceId={selectedDeviceId}
              onSelectDevice={setSelectedDevice}
            />
          </div>
          <div className="lg:col-span-3">
            {selectedDeviceId ? (
              <DeviceDrilldown deviceId={selectedDeviceId} />
            ) : (
              <EmptyDeviceDrilldown />
            )}
          </div>
        </div>
      )}

      {tab === "configuration" && <ConfigurationTab profile={p} />}

      {tab === "comparativa" && <TablaComparativa />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Header

function ProfileHeader({
  name,
  description,
  deviceType,
  collectionMethod,
  scheduleCron,
  fieldsCount,
  status,
  onPauseResume,
  onSnapshot,
  onDelete,
  onRollback,
  snapshotPending,
  toggleLoading,
  devicesCount,
}: {
  name: string;
  description: string;
  deviceType: string;
  collectionMethod: string;
  scheduleCron: string;
  fieldsCount: number;
  status: string;
  onPauseResume: () => void;
  onSnapshot: () => void;
  onDelete: () => void;
  onRollback: () => void;
  snapshotPending: boolean;
  toggleLoading: boolean;
  devicesCount: number;
}) {
  const isActive = status === "active";
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-xl font-semibold">{name}</h1>
            <span
              className={cn(
                "rounded-md px-2 py-0.5 text-[11px] font-medium",
                isActive
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                  : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
              )}
            >
              ● {isActive ? "Activo" : "Pausado"}
            </span>
          </div>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {deviceType} · {collectionMethod} ·{" "}
            <span title={scheduleCron}>{describeCron(scheduleCron)}</span> ·{" "}
            {fieldsCount} campos auditados
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onPauseResume}
            disabled={toggleLoading}
            className="inline-flex items-center gap-1 rounded-md border px-3 py-2 text-xs font-medium transition-colors hover:bg-accent disabled:opacity-50"
          >
            {toggleLoading ? (
              <Loader2 className="size-3 animate-spin" />
            ) : isActive ? (
              <Pause className="size-3" />
            ) : (
              <Play className="size-3" />
            )}
            {isActive ? "Pausar" : "Reanudar"}
          </button>
          <button
            type="button"
            onClick={onSnapshot}
            disabled={snapshotPending}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {snapshotPending ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" />
            )}
            Snapshot ahora
          </button>
          <button
            type="button"
            onClick={onRollback}
            disabled={devicesCount === 0}
            className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:text-amber-400"
            title={devicesCount === 0 ? "El perfil no tiene equipos" : ""}
          >
            <RotateCcw className="size-3" />
            Revertir perfil
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 rounded-md border border-destructive/30 px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            <Trash2 className="size-3" />
            Borrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tabs nav

function TabsNav({
  tab,
  setTab,
}: {
  tab: "devices" | "configuration" | "comparativa";
  setTab: (t: "devices" | "configuration" | "comparativa") => void;
}) {
  return (
    <div role="tablist" className="flex gap-1 border-b">
      <TabButton active={tab === "devices"} onClick={() => setTab("devices")}>
        Equipos
      </TabButton>
      <TabButton
        active={tab === "configuration"}
        onClick={() => setTab("configuration")}
      >
        Configuración
      </TabButton>
      <TabButton
        active={tab === "comparativa"}
        onClick={() => setTab("comparativa")}
      >
        Tabla comparativa
      </TabButton>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab Equipos — lista + drilldown

function ProfileDevicesPanel({
  profileId,
  selectedDeviceId,
  onSelectDevice,
}: {
  profileId: string;
  selectedDeviceId: string | null;
  onSelectDevice: (id: string) => void;
}) {
  const devices = useProfileDevices(profileId);
  const items = devices.data?.items ?? [];

  // E1: filtro por agencia/sitio. Las agencias salen de los propios devices
  // (cada uno trae `site_name` del join con DeviceSite); los que no tienen
  // sitio caen en "Sin sitio". Filtrado client-side: el endpoint ya devolvió
  // los devices del perfil.
  const ALL = "";
  const NO_SITE = "Sin sitio";
  const [siteFilter, setSiteFilter] = useState<string>(ALL);

  const siteNames = useMemo(() => {
    const set = new Set<string>();
    for (const d of items) set.add(d.site_name ?? NO_SITE);
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [items]);

  const filtered = useMemo(
    () =>
      siteFilter === ALL
        ? items
        : items.filter((d) => (d.site_name ?? NO_SITE) === siteFilter),
    [items, siteFilter],
  );

  return (
    <div className="rounded-md border bg-card">
      <div className="space-y-2 border-b p-3">
        <div>
          <h2 className="text-sm font-medium">Equipos del perfil</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {siteFilter === ALL
              ? `${items.length} equipos`
              : `${filtered.length} de ${items.length} equipos`}{" "}
            · click para ver historial
          </p>
        </div>
        {siteNames.length > 1 && (
          <select
            aria-label="Filtrar por agencia"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value={ALL}>Todas las agencias ({items.length})</option>
            {siteNames.map((name) => (
              <option key={name} value={name}>
                {name} (
                {items.filter((d) => (d.site_name ?? NO_SITE) === name).length})
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="max-h-[calc(100vh-380px)] overflow-y-auto">
        {devices.isPending && !devices.data ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            Cargando…
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            Sin equipos en este perfil todavía.
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            Sin equipos en esta agencia.
          </div>
        ) : (
          <ul>
            {filtered.map((d) => (
              <DeviceRow
                key={d.id}
                device={d}
                selected={d.id === selectedDeviceId}
                onSelect={() => onSelectDevice(d.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function DeviceRow({
  device,
  selected,
  onSelect,
}: {
  device: ConfigDevice;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex w-full items-center gap-3 border-b px-3 py-2 text-left transition-colors",
          selected ? "bg-primary/10" : "hover:bg-muted/40",
        )}
      >
        <StatusDot status={device.status} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">
            {device.hostname ?? device.ip}
          </div>
          <div className="truncate font-mono text-xs text-muted-foreground">
            {device.ip} · {device.role ?? "—"}
            {device.site_name ? ` · ${device.site_name}` : ""}
          </div>
        </div>
        <ChevronRight className="size-4 text-muted-foreground" />
      </button>
    </li>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === "online"
      ? "bg-emerald-500"
      : status === "offline"
        ? "bg-destructive"
        : "bg-muted-foreground";
  return (
    <span
      className={cn("inline-block size-2 rounded-full", color)}
      aria-label={`Estado: ${status}`}
    />
  );
}

function EmptyDeviceDrilldown() {
  return (
    <div className="flex h-full min-h-[300px] items-center justify-center rounded-md border bg-card p-8 text-center">
      <div>
        <p className="text-sm font-medium">Selecciona un equipo</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Click sobre uno de la izquierda para ver su historial de
          configuración, comparar versiones y capturar nuevos snapshots.
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab Configuracion (read-only)

function ConfigurationTab({
  profile,
}: {
  profile: {
    name: string;
    description: string;
    device_type: string;
    collection_method: string;
    credentials_ref: string;
    fields_to_audit: string[];
    schedule_cron: string;
    zabbix_host_group: string | null;
  };
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium">Configuración del perfil</h3>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <Row label="Nombre" value={profile.name} />
          <Row label="Tipo de equipo" value={profile.device_type} />
          <Row
            label="Método de recolección"
            value={profile.collection_method}
          />
          <Row label="Credenciales" value={profile.credentials_ref} />
          <Row
            label="Programación"
            value={describeCron(profile.schedule_cron)}
          />
          <Row
            label="Host group Zabbix"
            value={profile.zabbix_host_group ?? "—"}
            mono
          />
        </dl>
      </div>

      <div className="rounded-md border bg-card p-4">
        <h3 className="mb-2 text-sm font-medium">
          Campos auditados ({profile.fields_to_audit.length})
        </h3>
        <div className="flex flex-wrap gap-1">
          {profile.fields_to_audit.map((f) => (
            <span
              key={f}
              className="rounded-md border bg-muted/30 px-2 py-1 font-mono text-[11px]"
            >
              {f}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className={cn("font-medium", mono && "font-mono text-xs")}>
        {value}
      </dd>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Drilldown del device (reusa ADR_011: historial + diff + mark good)

function DeviceDrilldown({ deviceId }: { deviceId: string }) {
  const device = useConfigDevice(deviceId);
  const configs = useConfigDeviceConfigs(deviceId, { per_page: 50 });
  const diff = useConfigDeviceDiff(deviceId);
  const snapshotMut = useTakeSnapshot();
  const markGoodMut = useMarkGood();
  const rollbackDeviceMut = useRollbackDevice(deviceId);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);

  const handleSnapshot = async () => {
    setFeedback(null);
    try {
      const res = await snapshotMut.mutateAsync(deviceId);
      setFeedback(`Snapshot capturado: ${shortId(res.id)}`);
    } catch (err) {
      setFeedback(
        err instanceof ApiError ? err.message : "Error al capturar snapshot.",
      );
    }
  };

  const handleRollback = async ({
    totpCode,
    notes,
  }: {
    totpCode: string;
    notes: string;
  }) => {
    setRollbackError(null);
    try {
      await rollbackDeviceMut.mutateAsync({
        totp_code: totpCode,
        notes: notes || null,
      });
      setRollbackDialogOpen(false);
      setFeedback("Rollback agendado. Se aplicará en el siguiente ciclo.");
    } catch (err) {
      setRollbackError(
        err instanceof ApiError ? err.message : "Error al agendar rollback.",
      );
    }
  };

  if (device.isPending && !device.data) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center rounded-md border bg-card">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!device.data) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        No se pudo cargar el equipo.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <DeviceHeader
        ip={device.data.ip}
        hostname={device.data.hostname}
        role={device.data.role}
        model={device.data.model}
        status={device.data.status}
        onSnapshot={handleSnapshot}
        onRollback={() => {
          setRollbackError(null);
          setRollbackDialogOpen(true);
        }}
        snapshotPending={snapshotMut.isPending}
      />

      <RollbackDialog
        open={rollbackDialogOpen}
        onClose={() => setRollbackDialogOpen(false)}
        onConfirm={handleRollback}
        isSubmitting={rollbackDeviceMut.isPending}
        errorMessage={rollbackError}
        targetLabel={`${device.data.hostname ?? device.data.ip} · ${device.data.role ?? "sin rol"}`}
        helperText="Vuelve a la versión known_good de este equipo. Se aplicará en el siguiente ciclo del worker."
      />
      {feedback && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          {feedback}
        </div>
      )}
      <ConfigsHistoryList
        configs={configs.data?.items ?? []}
        onMarkGood={(configId) =>
          markGoodMut
            .mutateAsync({ deviceId, configId })
            .then(() => setFeedback("Marcada como buena conocida."))
            .catch((err) =>
              setFeedback(
                err instanceof ApiError
                  ? err.message
                  : "Error al marcar como buena.",
              ),
            )
        }
        markGoodPending={markGoodMut.isPending}
      />
      <DiffPanel
        loading={diff.isPending}
        changes={diff.data?.changes ?? []}
        summary={
          diff.data?.summary ?? {
            added: 0,
            removed: 0,
            changed: 0,
            type_changed: 0,
          }
        }
        warning={diff.data?.warning}
      />
    </div>
  );
}

function DeviceHeader({
  ip,
  hostname,
  role,
  model,
  status,
  onSnapshot,
  onRollback,
  snapshotPending,
}: {
  ip: string;
  hostname: string | null;
  role: string | null;
  model: string | null;
  status: string;
  onSnapshot: () => void;
  onRollback: () => void;
  snapshotPending: boolean;
}) {
  const cls =
    status === "online"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      : status === "offline"
        ? "bg-destructive/15 text-destructive"
        : "bg-muted text-muted-foreground";
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-base font-semibold">
              {hostname ?? ip}
            </h2>
            <span
              className={cn(
                "rounded-md px-2 py-0.5 text-[11px] font-medium",
                cls,
              )}
            >
              ● {status}
            </span>
          </div>
          <p className="font-mono text-xs text-muted-foreground">
            {ip} · {role ?? "—"}
          </p>
          {model && (
            <p className="mt-1 text-xs text-muted-foreground">{model}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onSnapshot}
            disabled={snapshotPending}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {snapshotPending ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" />
            )}
            Snapshot ahora
          </button>
          <button
            type="button"
            onClick={onRollback}
            className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-500/20 dark:text-amber-400"
          >
            <RotateCcw className="size-3" />
            Revertir equipo
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfigsHistoryList({
  configs,
  onMarkGood,
  markGoodPending,
}: {
  configs: ConfigDeviceConfigSummary[];
  onMarkGood: (configId: string) => void;
  markGoodPending: boolean;
}) {
  return (
    <div className="rounded-md border bg-card">
      <div className="border-b px-3 py-2">
        <h3 className="text-sm font-medium">Historial de snapshots</h3>
      </div>
      {configs.length === 0 ? (
        <div className="p-6 text-center text-xs text-muted-foreground">
          Sin snapshots todavía.
        </div>
      ) : (
        <ul className="max-h-[200px] overflow-y-auto">
          {configs.map((c) => (
            <li
              key={c.id}
              className="flex items-center gap-3 border-b px-3 py-2 text-xs last:border-b-0"
            >
              {c.is_known_good ? (
                <Star className="size-3.5 fill-amber-400 text-amber-500" />
              ) : (
                <Star className="size-3.5 text-muted-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate">
                  {new Date(c.captured_at).toLocaleString()}
                </div>
                <div className="truncate text-muted-foreground">
                  {c.captured_by}
                </div>
              </div>
              {!c.is_known_good && (
                <button
                  type="button"
                  onClick={() => onMarkGood(c.id)}
                  disabled={markGoodPending}
                  className="rounded-md border px-2 py-1 text-[11px] font-medium hover:bg-accent disabled:opacity-50"
                >
                  Marcar buena
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DiffPanel({
  loading,
  changes,
  summary,
  warning,
}: {
  loading: boolean;
  changes: DiffChange[];
  summary: {
    added: number;
    removed: number;
    changed: number;
    type_changed: number;
  };
  warning?: string;
}) {
  const total = useMemo(
    () =>
      summary.added + summary.removed + summary.changed + summary.type_changed,
    [summary],
  );

  return (
    <div className="rounded-md border bg-card">
      <div className="border-b px-3 py-2">
        <h3 className="text-sm font-medium">Diff vs versión buena conocida</h3>
      </div>
      {warning === "no_baseline" && (
        <div className="m-3 flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-xs">
          <AlertCircle className="mt-0.5 size-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            Marca una snapshot como "buena" para empezar a ver diffs.
          </p>
        </div>
      )}
      {loading && (
        <div className="p-6 text-center text-xs text-muted-foreground">
          Calculando…
        </div>
      )}
      {!loading && warning !== "no_baseline" && total === 0 && (
        <div className="p-6 text-center text-xs text-muted-foreground">
          Sin cambios contra la versión buena.
        </div>
      )}
      {!loading && total > 0 && (
        <ul className="max-h-[400px] divide-y overflow-y-auto">
          {changes.map((c, i) => (
            <DiffRow key={i} change={c} />
          ))}
        </ul>
      )}
    </div>
  );
}

function DiffRow({ change }: { change: DiffChange }) {
  return (
    <li className="px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium">
          {change.type}
        </span>
        <span className="truncate font-mono text-muted-foreground">
          {formatPath(change.path)}
        </span>
      </div>
      <div className="mt-1 grid grid-cols-2 gap-2">
        {change.type !== "added" && (
          <div className="rounded-md border bg-rose-500/5 p-2">
            <div className="text-[10px] uppercase text-muted-foreground">
              Antes
            </div>
            <div className="break-all font-mono">{formatValue(change.old)}</div>
          </div>
        )}
        {change.type !== "removed" && (
          <div
            className={cn(
              "rounded-md border bg-emerald-500/5 p-2",
              change.type === "added" && "col-span-2",
            )}
          >
            <div className="text-[10px] uppercase text-muted-foreground">
              Después
            </div>
            <div className="break-all font-mono">{formatValue(change.new)}</div>
          </div>
        )}
      </div>
    </li>
  );
}
